'use client';

import React, { useEffect, useState } from 'react';
import { ApiRequestConfig, DataFetcher } from '../utils/DataFetcher';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { getNestedValue } from '../utils/dataBinding';
import ComponentRenderer from './ComponentRenderer';

interface RenderContext {
    companyId?: string;
    templateId?: string;
    domain?: string;
    pageSlug?: string;
    params?: Record<string, any>;
    searchParams?: Record<string, any>;
    store_slug?: string;
    slug_type?: string;
    category_slug?: string;
    promotion_slug?: string;
    out_link?: string;
}

interface PaginationMeta {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
}

function arrayFromUnknown(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    return [];
}

/** Mirrors LoadMoreCardList's own resolveDataSourceArray (see its doc comment) — same narrow scope, not a full resolveCollection reimplementation. */
function resolveDataSourceArray(responseData: any, dataSourceKey: string | undefined): any[] {
    if (!dataSourceKey) return [];
    const keyed =
        getNestedValue(responseData, dataSourceKey) ??
        getNestedValue(responseData?.data, dataSourceKey) ??
        getNestedValue(responseData?.data?.[0], dataSourceKey);
    return arrayFromUnknown(keyed);
}

function readPaginationMeta(response: unknown, paginationKey: string): PaginationMeta | undefined {
    const pagination = (
        getNestedValue(response, paginationKey) ??
        getNestedValue(response, "pagination") ??
        getNestedValue(response, "data.pagination")
    ) as Partial<PaginationMeta> | undefined;
    if (!pagination) return undefined;
    const { total, per_page, current_page, last_page } = pagination;
    if (![total, per_page, current_page, last_page].every((value) => Number.isFinite(Number(value)))) return undefined;
    return { total: Number(total), per_page: Number(per_page), current_page: Number(current_page), last_page: Number(last_page) };
}

/** Page numbers to render around the current page, condensed with "…" past 7 pages — same windowing the builder's PaginationList.tsx uses, so canvas/Preview/live all show the same pager shape. */
function pageWindow(current: number, last: number): Array<number | 'ellipsis'> {
    if (last <= 7) return Array.from({ length: last }, (_, index) => index + 1);
    const pages = new Set<number>([1, last, current, current - 1, current + 1]);
    const sorted = [...pages].filter((page) => page >= 1 && page <= last).sort((a, b) => a - b);
    const out: Array<number | 'ellipsis'> = [];
    sorted.forEach((page, index) => {
        if (index > 0 && page - sorted[index - 1] > 1) out.push('ellipsis');
        out.push(page);
    });
    return out;
}

interface PaginatedCardListProps {
    /** The repeated CARD node template (carries `pagination`, `dataSourceKey`). */
    cardTemplate: ComponentNode;
    /** Styles the button row (builderPart: "pagination"), set by the builder's Style tab. */
    paginationTemplate?: ComponentNode;
    /** Page 1's items, already resolved server-side by DynamicSectionComponent. */
    initialItems: any[];
    /** Page 1's raw API response, read for its `pagination` object only. */
    initialResponse: any;
    /** The section's apiConfig, reused (same endpoint/body) for each clicked page. */
    apiConfig: ApiRequestConfig;
    gridStyle: React.CSSProperties;
    context?: RenderContext;
}

/**
 * The storefront counterpart to theme-builder's PaginationList.tsx — a
 * numbered Prev/1/2/…/Next pager that REPLACES the shown page's items on
 * click, distinct from LoadMoreCardList (which APPENDS on a "Show More"
 * click and stays scoped to Banner Offers' sidebar use case).
 */
const PaginatedCardList: React.FC<PaginatedCardListProps> = ({
    cardTemplate,
    paginationTemplate,
    initialItems,
    initialResponse,
    apiConfig,
    gridStyle,
    context,
}) => {
    const pagination = cardTemplate.pagination!;
    const [items, setItems] = useState<any[]>(initialItems);
    const [meta, setMeta] = useState<PaginationMeta | undefined>(() => readPaginationMeta(initialResponse, pagination.paginationKey));
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setItems(initialItems);
        setMeta(readPaginationMeta(initialResponse, pagination.paginationKey));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialResponse]);

    const goToPage = async (page: number) => {
        if (isLoading || page === meta?.current_page) return;
        setIsLoading(true);
        try {
            const nextRequest: ApiRequestConfig = {
                ...apiConfig,
                body: {
                    ...(apiConfig.body ?? {}),
                    [pagination.param]: page,
                    [pagination.pageSizeParam]: pagination.pageSize,
                },
            };

            const response = await DataFetcher.fetchRawData(nextRequest, {
                companyId: context?.companyId,
                domain: context?.domain,
            });

            setItems(resolveDataSourceArray(response, cardTemplate.dataSourceKey));
            setMeta(readPaginationMeta(response, pagination.paginationKey));
        } catch {
            // Leave items/meta as they were — the pager just stays on the current page.
        } finally {
            setIsLoading(false);
        }
    };

    const pagerStyle = convertStylesToInline(paginationTemplate?.styles);
    // Longhand border properties throughout (never the `border` shorthand) —
    // an author's Style-tab border color lands in pagerStyle and is spread
    // last; mixing it with a shorthand `border` here triggers React's
    // "Removing a style property during rerender" warning when the
    // active/disabled state toggles.
    const buttonBase: React.CSSProperties = {
        minWidth: '36px',
        height: '36px',
        padding: '0 10px',
        borderRadius: '999px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#e2e8f0',
        background: '#ffffff',
        color: '#0f172a',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
    };
    const activeStyle: React.CSSProperties = {
        background: 'var(--theme-color-primary, #f23017)',
        borderColor: 'var(--theme-color-primary, #f23017)',
        color: '#ffffff',
    };
    const disabledStyle: React.CSSProperties = { opacity: 0.4, cursor: 'default' };

    return (
        <>
            <div style={gridStyle} className="cb-responsive-grid">
                {items.map((item, index) => (
                    <ComponentRenderer
                        key={`${cardTemplate.id}-${index}`}
                        component={cardTemplate}
                        data={item}
                        context={context}
                    />
                ))}
            </div>
            {meta && meta.last_page > 1 && pagination.variant === 'loadMoreButton' && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', ...pagerStyle }}>
                    <button
                        type="button"
                        onClick={() => goToPage(meta.current_page + 1)}
                        disabled={isLoading || meta.current_page >= meta.last_page}
                        style={{ ...buttonBase, minWidth: '140px', ...(meta.current_page >= meta.last_page ? disabledStyle : {}) }}
                    >
                        {isLoading ? 'Loading…' : 'Show more'}
                    </button>
                </div>
            )}
            {meta && meta.last_page > 1 && pagination.variant !== 'loadMoreButton' && (
                <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '20px', flexWrap: 'wrap', ...pagerStyle }}
                >
                    <button
                        type="button"
                        onClick={() => goToPage(meta.current_page - 1)}
                        disabled={isLoading || meta.current_page <= 1}
                        style={{ ...buttonBase, ...(meta.current_page <= 1 ? disabledStyle : {}) }}
                    >
                        {pagination.variant === 'arrows' ? '<' : 'Prev'}
                    </button>
                    {pagination.variant === 'arrows' ? (
                        <span style={{ ...buttonBase, borderStyle: 'none', background: 'transparent', cursor: 'default' }}>{meta.current_page}</span>
                    ) : (
                        pageWindow(meta.current_page, meta.last_page).map((page, index) =>
                            page === 'ellipsis' ? (
                                <span key={`ellipsis-${index}`} style={{ padding: '0 4px', color: '#94a3b8' }}>
                                    …
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    type="button"
                                    onClick={() => goToPage(page)}
                                    disabled={isLoading}
                                    style={{ ...buttonBase, ...(page === meta.current_page ? activeStyle : {}) }}
                                >
                                    {page}
                                </button>
                            ),
                        )
                    )}
                    <button
                        type="button"
                        onClick={() => goToPage(meta.current_page + 1)}
                        disabled={isLoading || meta.current_page >= meta.last_page}
                        style={{ ...buttonBase, ...(meta.current_page >= meta.last_page ? disabledStyle : {}) }}
                    >
                        {pagination.variant === 'arrows' ? '>' : 'Next'}
                    </button>
                </div>
            )}
        </>
    );
};

export default PaginatedCardList;
